import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertOctagon, CalendarClock, FileWarning, PenLine, RefreshCcw, Scale, ShieldAlert, ShieldCheck, Euro, ListChecks } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { userName, useUsers } from "../../lib/hooks";
import { KpiCard, PageHeader } from "../../components/common";
import { Button } from "../../components/ui/button";

function Section({ num, title, children, testid }) {
  return (
    <section className="mt-8" data-testid={testid}>
      <div className="mb-3 flex items-baseline gap-3">
        <span className="font-mono text-xs font-semibold text-slate-400">{num}</span>
        <h2 className="ts-section-title">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export default function LegalDashboard() {
  const qc = useQueryClient();
  const { map } = useUsers();
  const { data: d, isLoading } = useQuery({
    queryKey: ["legal-dashboard"],
    queryFn: async () => (await api.get("/legal/dashboard")).data,
  });

  const runEngine = async () => {
    try {
      const r = await api.post("/legal/date-engine/run");
      toast.success("Motor de fechas ejecutado", { description: JSON.stringify(r.data) });
      qc.invalidateQueries({ queryKey: ["legal-dashboard"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  if (isLoading || !d) {
    return <div className="flex h-64 items-center justify-center" data-testid="legal-dashboard-loading"><div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-700" /></div>;
  }

  return (
    <div data-testid="legal-dashboard-page">
      <PageHeader
        title="Contratos y Legal — Cuadro de mando"
        subtitle="Todos los indicadores permiten drill-down hasta los elementos que los generan."
        actions={<Button variant="outline" size="sm" onClick={runEngine} data-testid="run-date-engine"><RefreshCcw className="mr-1 h-3.5 w-3.5" /> Ejecutar motor de fechas (demo)</Button>}
      />

      <Section num="01" title="Requiere atención" testid="legal-atencion">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <KpiCard title="Pendientes de mi validación" value={d.attention.mi_validacion} icon={Scale} tone={d.attention.mi_validacion ? "warning" : "muted"} to="/legal/contratos?estado=pendiente_validacion" testid="legal-kpi-validacion" />
          <KpiCard title="Aprobaciones pendientes" value={d.attention.aprobaciones_pendientes} icon={ShieldCheck} tone={d.attention.aprobaciones_pendientes ? "ai" : "muted"} to="/legal/contratos?estado=pendiente_aprobacion" testid="legal-kpi-aprobaciones" />
          <KpiCard title="Firmas pendientes" value={d.attention.firmas_pendientes} icon={PenLine} tone={d.attention.firmas_pendientes ? "info" : "muted"} to="/legal/firmas" testid="legal-kpi-firmas" />
          <KpiCard title="Obligaciones vencidas" value={d.attention.obligaciones_vencidas} icon={ListChecks} tone={d.attention.obligaciones_vencidas ? "danger" : "muted"} to="/legal/obligaciones?estado=vencida" testid="legal-kpi-obligaciones" />
          <KpiCard title="Vencimientos 90 días" value={d.attention.vencimientos_90} icon={CalendarClock} tone={d.attention.vencimientos_90 ? "warning" : "muted"} to="/legal/contratos?vencimiento=90" testid="legal-kpi-vencimientos" />
          <KpiCard title="Riesgos críticos" value={d.attention.riesgos_criticos} icon={ShieldAlert} tone={d.attention.riesgos_criticos ? "danger" : "muted"} to="/legal/contratos?riesgo=alto" testid="legal-kpi-riesgos" />
          <KpiCard title="Evidencias en revisión" value={d.attention.evidencias_pendientes} icon={FileWarning} tone={d.attention.evidencias_pendientes ? "ai" : "muted"} to="/legal/obligaciones?estado=evidencia_en_revision" testid="legal-kpi-evidencias" />
          <KpiCard title="Contratos bloqueados" value={d.attention.bloqueados} icon={AlertOctagon} tone={d.attention.bloqueados ? "danger" : "muted"} to="/legal/contratos?estado=bloqueado" testid="legal-kpi-bloqueados" />
        </div>
      </Section>

      <Section num="02" title="Cartera" testid="legal-cartera">
        <div className="grid gap-3 md:grid-cols-4">
          <Link to="/legal/contratos?estado=activo" className="ts-card ts-card-hover p-4" data-testid="cartera-activos">
            <p className="ts-caption">Contratos activos</p>
            <p className="font-display text-3xl font-bold text-emerald-600">{d.cartera.activos}</p>
            <p className="mt-1 text-xs text-slate-500">Valor contratado: {(d.cartera.valor_activo / 1000000).toFixed(1)} M€</p>
          </Link>
          <Link to="/legal/nda-loi" className="ts-card ts-card-hover p-4" data-testid="cartera-nda-loi">
            <p className="ts-caption">NDA / LOI</p>
            <p className="font-display text-3xl font-bold text-slate-900">{d.cartera.ndas} / {d.cartera.lois}</p>
            <p className="mt-1 text-xs text-slate-500">Total contratos: {d.cartera.total}</p>
          </Link>
          <div className="ts-card p-4 md:col-span-2" data-testid="cartera-por-tipo">
            <p className="ts-caption mb-2">Por tipo</p>
            <div className="flex flex-wrap gap-1.5">
              {d.cartera.por_tipo.slice(0, 8).map((t) => (
                <Link key={t.tipo} to={`/legal/contratos?tipo=${encodeURIComponent(t.tipo)}`} className="ts-chip ts-chip-muted hover:border-slate-400" data-testid={`cartera-tipo-${t.n}`}>
                  {t.tipo} · {t.n}
                </Link>
              ))}
            </div>
            <p className="ts-caption mb-1 mt-3">Por responsable</p>
            <div className="flex flex-wrap gap-1.5">
              {d.cartera.por_responsable.slice(0, 6).map((o) => (
                <Link key={o.owner_id} to={`/legal/contratos?owner=${o.owner_id}`} className="ts-chip ts-chip-muted hover:border-slate-400">
                  {userName(map, o.owner_id)} · {o.n}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <div className="grid gap-6 xl:grid-cols-2">
        <Section num="03" title="Vencimientos y renovaciones" testid="legal-vencimientos">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <KpiCard title="30 días" value={d.vencimientos.d30} tone="danger" to="/legal/contratos?vencimiento=30" testid="venc-30" />
            <KpiCard title="60 días" value={d.vencimientos.d60} tone="warning" to="/legal/contratos?vencimiento=60" testid="venc-60" />
            <KpiCard title="90 días" value={d.vencimientos.d90} tone="warning" to="/legal/contratos?vencimiento=90" testid="venc-90" />
            <KpiCard title="Preavisos" value={d.vencimientos.preavisos} tone="danger" to="/legal/contratos?preaviso=30" testid="venc-preavisos" />
            <KpiCard title="Renovación automática" value={d.vencimientos.renovacion_automatica} tone="ai" to="/legal/contratos" testid="venc-auto" />
          </div>
        </Section>

        <Section num="04" title="Obligaciones" testid="legal-obligaciones">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <KpiCard title="Abiertas" value={d.obligaciones.abiertas} tone="info" to="/legal/obligaciones" testid="obl-abiertas" />
            <KpiCard title="Vencidas" value={d.obligaciones.vencidas} tone="danger" to="/legal/obligaciones?estado=vencida" testid="obl-vencidas" />
            <KpiCard title="Próximas" value={d.obligaciones.proximas} tone="warning" to="/legal/obligaciones?estado=proxima" testid="obl-proximas" />
            <KpiCard title="Evidencia rechazada" value={d.obligaciones.evidencia_rechazada} tone="danger" to="/legal/obligaciones?estado=pendiente_evidencia" testid="obl-rechazada" />
            <KpiCard title="Alto riesgo" value={d.obligaciones.alto_riesgo} tone="warning" to="/legal/obligaciones?riesgo=alto" testid="obl-riesgo" />
          </div>
        </Section>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <Section num="05" title="Firmas (DocuSign mock)" testid="legal-firmas">
          <div className="ts-card grid grid-cols-3 gap-2 p-4">
            {[["preparado", "Preparadas"], ["enviado", "Enviadas"], ["parcialmente_firmado", "Parciales"], ["completado", "Completadas"], ["rechazado", "Rechazadas"], ["visualizado", "Visualizadas"]].map(([k, label]) => (
              <Link key={k} to={`/legal/firmas?estado=${k}`} className="text-center" data-testid={`firmas-${k}`}>
                <p className="font-display text-xl font-bold text-slate-800">{d.firmas[k] || 0}</p>
                <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
              </Link>
            ))}
          </div>
        </Section>

        <Section num="06" title="Riesgo" testid="legal-riesgo">
          <div className="ts-card grid grid-cols-3 gap-2 p-4 sm:grid-cols-5">
            <div className="text-center"><p className="font-display text-xl font-bold text-red-600">{d.riesgo.critico}</p><p className="text-[10px] uppercase text-slate-400">Crítico</p></div>
            <div className="text-center"><p className="font-display text-xl font-bold text-amber-600">{d.riesgo.alto}</p><p className="text-[10px] uppercase text-slate-400">Alto</p></div>
            <div className="text-center"><p className="font-display text-xl font-bold text-slate-600">{d.riesgo.medio}</p><p className="text-[10px] uppercase text-slate-400">Medio</p></div>
            <div className="text-center"><p className="font-display text-xl font-bold text-sky-600">{d.riesgo.aceptado}</p><p className="text-[10px] uppercase text-slate-400">Aceptado</p></div>
            <div className="text-center"><p className="font-display text-xl font-bold text-red-500">{d.riesgo.mitigacion}</p><p className="text-[10px] uppercase text-slate-400">Mitigación</p></div>
          </div>
        </Section>

        <Section num="07" title="Economía (calendario de hitos)" testid="legal-economia">
          <div className="ts-card grid grid-cols-3 gap-2 p-4">
            <div className="text-center"><p className="font-display text-xl font-bold text-slate-800">{(d.economia.valor_activo / 1000000).toFixed(1)} M€</p><p className="text-[10px] uppercase text-slate-400">Valor activo</p></div>
            <Link to="/legal/contratos" className="text-center" data-testid="eco-proximo"><p className="font-display text-xl font-bold text-emerald-600">{(d.economia.proximo_facturar / 1000).toFixed(0)} k€</p><p className="text-[10px] uppercase text-slate-400">Próximo a facturar</p></Link>
            <Link to="/legal/contratos" className="text-center" data-testid="eco-bloqueados"><p className="font-display text-xl font-bold text-red-600">{d.economia.hitos_bloqueados}</p><p className="text-[10px] uppercase text-slate-400">Hitos bloqueados</p></Link>
          </div>
        </Section>
      </div>
    </div>
  );
}
